import { createRootRoute, createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { AutolinksTuning } from "./features/admin/AutolinksTuning";
import { EvalsDashboard } from "./features/admin/EvalsDashboard";
import { GraphView } from "./features/admin/GraphView";
import { IngestDashboard } from "./features/admin/IngestDashboard";
import { ArtifactPage } from "./features/artifacts/ArtifactPage";
import { ChatPage } from "./features/chat/ChatPage";
import { CollectionView } from "./features/collections/CollectionView";
import { CollectionsList } from "./features/collections/CollectionsList";
import { ComposePage } from "./features/compose/ComposePage";
import { DocEditor } from "./features/editor/DocEditor";
import { HighlightsInbox } from "./features/highlights/HighlightsInbox";
import { UploadPage } from "./features/intake/UploadPage";
import { NotebookDetail } from "./features/notebooks/NotebookDetail";
import { NotebooksList } from "./features/notebooks/NotebooksList";
import { SearchPage } from "./features/search/SearchPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { AppShell } from "./features/shell/AppShell";
import { DocViewer } from "./features/viewer/DocViewer";
import { DocsList } from "./routes/docs";
import { HomePage } from "./routes/index";

export const rootRoute = createRootRoute({ component: AppShell });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const docsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  validateSearch: z.object({
    project: z.string().optional(),
    kind: z.string().optional(),
    lang: z.string().optional(),
  }),
  component: DocsList,
});

const docViewerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$docId",
  component: DocViewer,
});

const docEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$docId/edit",
  component: DocEditor,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: SearchPage,
});

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat",
  validateSearch: z.object({
    q: z.string().optional(),
    scope: z.enum(["doc", "col", "all"]).optional(),
  }),
  component: ChatPage,
});

const chatSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chat/$sessionId",
  component: ChatPage,
});

const notebooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notebooks",
  component: NotebooksList,
});

const notebookDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/notebooks/$notebookId",
  component: NotebookDetail,
});

const uploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/upload",
  component: UploadPage,
});

const composeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/compose",
  component: ComposePage,
});

const artifactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/artifacts/$artifactId",
  component: ArtifactPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const settingsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/$section",
  component: SettingsPage,
});

const highlightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/highlights",
  component: HighlightsInbox,
});

const collectionsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collections",
  validateSearch: z.object({ tag: z.string().optional() }),
  component: CollectionsList,
});

const collectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/collections/$slug",
  component: CollectionView,
});

// 301-like redirect /projects/:slug → /collections/:slug
const projectsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects/$slug",
  beforeLoad: ({ params }: { params: { slug: string } }) => {
    if (typeof window !== "undefined") {
      window.location.replace(`/collections/${encodeURIComponent(params.slug)}`);
    }
  },
  component: () => null,
});

const adminIngestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/ingest",
  component: IngestDashboard,
});
const adminGraphRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/graph",
  component: GraphView,
});
// uiv2 decisión 3 (2026-05-25): graph es pantalla principal en /graph.
const graphRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/graph",
  component: GraphView,
});
const adminAutolinksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/autolinks",
  component: AutolinksTuning,
});
const adminEvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/evals",
  component: EvalsDashboard,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  docsListRoute,
  docViewerRoute,
  docEditorRoute,
  searchRoute,
  chatRoute,
  chatSessionRoute,
  notebooksRoute,
  notebookDetailRoute,
  uploadRoute,
  composeRoute,
  artifactRoute,
  highlightsRoute,
  collectionsListRoute,
  collectionDetailRoute,
  projectsRedirectRoute,
  settingsRoute,
  settingsSectionRoute,
  adminIngestRoute,
  adminGraphRoute,
  graphRoute,
  adminAutolinksRoute,
  adminEvalsRoute,
]);
